import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuth0 } from '@auth0/auth0-react'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowLeftIcon,
  UsersIcon,
  BookOpenIcon,
  PhotoIcon,
  MinusIcon,
  PlusIcon,
  CheckIcon
} from '@heroicons/react/24/outline'
import { UpdateRailbirdGameData, useRailbirdGamesApi } from '../../services/railbirdGamesApi'
import { isAdmin } from '../../utils/roles'
import PhotoUpload from '../../components/PhotoUpload'
import toast from 'react-hot-toast'

export default function EditGamePage() {
  const { gameId } = useParams<{ gameId: string }>()
  const navigate = useNavigate()
  const { user, getAccessTokenSilently } = useAuth0()
  const railbirdGamesApi = useRailbirdGamesApi()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState<UpdateRailbirdGameData>({
    name: '',
    description: '',
    rules: '',
    maxPlayers: undefined,
    images: []
  })
  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  const {
    data: game,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['railbird-game', gameId],
    queryFn: () => railbirdGamesApi.getGame(gameId!, getAccessTokenSilently),
    enabled: !!gameId && !!user
  })

  const userIsAdmin = isAdmin(user)

  // Update form data when game loads
  useEffect(() => {
    if (game) {
      setFormData({
        name: game.name,
        description: game.description,
        rules: game.rules,
        maxPlayers: game.maxPlayers,
        images: game.images ? [...game.images] : []
      })
    }
  }, [game])

  // Redirect if not admin
  useEffect(() => {
    if (user && !userIsAdmin) {
      navigate(`/legacy/railbird-games/${gameId}`)
      toast.error('You do not have permission to edit games')
    }
  }, [user, userIsAdmin, navigate, gameId])

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {}

    if (!formData.name?.trim()) {
      newErrors.name = 'Game name is required'
    }

    if (!formData.description?.trim()) {
      newErrors.description = 'Description is required'
    }

    if (!formData.rules?.trim()) {
      newErrors.rules = 'Rules are required'
    }

    if (formData.maxPlayers && formData.maxPlayers < 1) {
      newErrors.maxPlayers = 'Max players must be at least 1'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm() || !game) {
      return
    }

    try {
      setIsSubmitting(true)
      await railbirdGamesApi.updateGame(game.id, formData)
      toast.success('Game updated successfully!')
      navigate(`/legacy/railbird-games/${game.id}`)
    } catch (error: any) {
      console.error('Update game error:', error)
      toast.error(error.message || 'Failed to update game')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleImageUpload = (urls: string[]) => {
    setFormData(prev => ({
      ...prev,
      images: [...(prev.images || []), ...urls]
    }))
  }

  const handleImageRemove = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images?.filter((_, i) => i !== index) || []
    }))
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
          <p className="text-amber-700 dark:text-amber-300" style={{ fontFamily: 'Georgia, serif' }}>
            Loading game...
          </p>
        </div>
      </div>
    )
  }

  if (error || !game) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center text-red-600 dark:text-red-400" style={{ fontFamily: 'Georgia, serif' }}>
          <p className="text-xl">Error loading game</p>
          <p className="mt-2">{error?.message || 'Game not found'}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-12">
          <button
            onClick={() => navigate(`/legacy/railbird-games/${game.id}`)}
            className="inline-flex items-center text-amber-700 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-100 mb-4"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            Back to Game Details
          </button>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-4xl font-bold text-amber-900 dark:text-amber-100 mb-2" style={{ fontFamily: 'Georgia, serif' }}>
              Edit Game
            </h1>
            <p className="text-amber-700 dark:text-amber-300" style={{ fontFamily: 'Georgia, serif' }}>
              Update the details for "{game.name}"
            </p>
          </motion.div>
        </div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-12"
        >
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Game Name */}
            <div>
              <label className="block text-sm font-medium text-amber-700 dark:text-amber-300 mb-2" style={{ fontFamily: 'Georgia, serif' }}>
                Game Name *
              </label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-4 py-3 border border-amber-300 dark:border-amber-600 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                style={{ fontFamily: 'Georgia, serif' }}
                placeholder="Enter the game name"
                required
              />
              {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-amber-700 dark:text-amber-300 mb-2" style={{ fontFamily: 'Georgia, serif' }}>
                Description *
              </label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={6}
                className="w-full px-4 py-3 border border-amber-300 dark:border-amber-600 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                style={{ fontFamily: 'Georgia, serif' }}
                placeholder="Describe this game..."
                required
              />
              {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
            </div>

            {/* Rules */}
            <div>
              <label className="block text-sm font-medium text-amber-700 dark:text-amber-300 mb-2" style={{ fontFamily: 'Georgia, serif' }}>
                Rules *
              </label>
              <textarea
                value={formData.rules || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, rules: e.target.value }))}
                rows={10}
                className="w-full px-4 py-3 border border-amber-300 dark:border-amber-600 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                style={{ fontFamily: 'Georgia, serif' }}
                placeholder="Enter the game rules..."
                required
              />
              {errors.rules && <p className="mt-1 text-sm text-red-600">{errors.rules}</p>}
            </div>

            {/* Max Players */}
            <div>
              <label className="block text-sm font-medium text-amber-700 dark:text-amber-300 mb-2" style={{ fontFamily: 'Georgia, serif' }}>
                Maximum Players
              </label>
              <div className="flex items-center space-x-4">
                <UsersIcon className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                <input
                  type="number"
                  value={formData.maxPlayers || ''}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    maxPlayers: e.target.value ? parseInt(e.target.value) : undefined 
                  }))}
                  min="1"
                  className="flex-1 px-4 py-3 border border-amber-300 dark:border-amber-600 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  style={{ fontFamily: 'Georgia, serif' }}
                  placeholder="Enter maximum number of players"
                />
              </div>
              {errors.maxPlayers && <p className="mt-1 text-sm text-red-600">{errors.maxPlayers}</p>}
            </div>

            {/* Images */}
            <div>
              <label className="block text-sm font-medium text-amber-700 dark:text-amber-300 mb-2" style={{ fontFamily: 'Georgia, serif' }}>
                Game Photos
              </label>
              
              {/* Existing Images */}
              {formData.images && formData.images.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                  {formData.images.map((image, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={image}
                        alt={`Game image ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => handleImageRemove(index)}
                        className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <MinusIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload New Images */}
              <PhotoUpload
                onUpload={handleImageUpload}
                multiple={true}
                className="border-2 border-dashed border-amber-300 dark:border-amber-600 rounded-lg p-6 text-center hover:border-amber-400 dark:hover:border-amber-500 transition-colors"
              >
                <PhotoIcon className="w-12 h-12 text-amber-400 dark:text-amber-500 mx-auto mb-4" />
                <p className="text-amber-700 dark:text-amber-300" style={{ fontFamily: 'Georgia, serif' }}>
                  Click to upload game photos
                </p>
              </PhotoUpload>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-4 pt-10">
              <button
                type="button"
                onClick={() => navigate(`/legacy/railbird-games/${game.id}`)}
                className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                <CheckIcon className="w-5 h-5" />
                <span>{isSubmitting ? 'Saving...' : 'Save Changes'}</span>
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  )
}
